import { Icon } from '@iconify/react';
import KeyboardArrowDownIcon from '@iconify/icons-eva/arrow-ios-downward-fill';
import KeyboardArrowUpIcon from '@iconify/icons-eva/arrow-ios-upward-fill';
import OpenInNewIcon from '@iconify/icons-ic/open-in-new';
import PropTypes from 'prop-types';

import {
  Grid,
  Container,
  Card,
  Collapse,
  Alert,
  Skeleton,
  useTheme,
  Typography,
  IconButton,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { useState, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import usePeriod from '../hooks/usePeriod';
import PeriodSelector from '../components/PeriodSelector';
import { useAccount } from '../hooks/useAccount';
import MetricCard from '../components/MetricCard';
import HelperTooltip from '../components/HelperTooltip';
import Page from '../components/Page';
import ContainerMessage from '../components/ContainerMessage';
import MultiChart from '../components/MultiChart';

const PAGE_SIZE = 10;

const GET_ADVANCED_STATISTICS = gql`
  query listAdvancedStatistics($domain: ID!, $from: String!, $to: String!) {
    listAdvancedStatistics(domain: $domain, typeDateKey: { between: [$from, $to] }) {
      items {
        domain
        typeDateKey
        type
        dateKey
        createdAt
        updatedAt
        rawSearchCount
        searchCount
        topQueries {
          query
          count
          ctr
          clickSessions
          __typename
          topClicked {
            productId
            count
            ctr
            __typename
          }
        }
        clickCount
        topProducts {
          productId
          count
          ctr
          __typename
        }
        __typename
      }
    }
  }
`;

const GET_PRODUCT_DETAIL = gql`
  query getProduct($domain: ID!, $productId: String!) {
    getProduct(domain: $domain, productId: $productId) {
      productId
      imgLink
      link
      title
      __typename
    }
  }
`;

function accummulateTopQueries(items) {
  const topQueriesOutput = [];

  if (items.length < 1) {
    return {
      topQueries: topQueriesOutput,
      totalCount: 0,
      totalRawSearchCount: 0,
      totalDays: 0,
      topQueriesChartData: {}
    };
  }

  const topQueries = {};
  let totalCount = 0;
  let totalRawSearchCount = 0;
  items.forEach((item) => {
    totalCount += item.searchCount;
    totalRawSearchCount += item.rawSearchCount;
    item.topQueries.forEach((topQuery) => {
      const dailyClickSessions =
        topQuery.clickSessions != null ? topQuery.clickSessions : topQuery.ctr * topQuery.count;
      if (!topQueries[topQuery.query]) {
        topQueries[topQuery.query] = {
          count: topQuery.count,
          clickSessions: dailyClickSessions,
          daysWithQuery: 1,
          topClicked: {}
        };
      } else {
        topQueries[topQuery.query].count += topQuery.count;
        topQueries[topQuery.query].clickSessions += dailyClickSessions;
        topQueries[topQuery.query].daysWithQuery += 1;
      }
      topQuery.topClicked.forEach((topClicked) => {
        if (!topQueries[topQuery.query].topClicked[topClicked.productId]) {
          topQueries[topQuery.query].topClicked[topClicked.productId] = topClicked.count;
        } else {
          topQueries[topQuery.query].topClicked[topClicked.productId] += topClicked.count;
        }
      });
    });
  });

  Object.keys(topQueries).forEach((query) => {
    const topClickedAccumulated = [];
    const totalClicksForQuery = Object.values(topQueries[query].topClicked).reduce(
      (a, b) => a + b,
      0
    );
    Object.keys(topQueries[query].topClicked).forEach((productId) => {
      topClickedAccumulated.push({
        productId,
        count: topQueries[query].topClicked[productId],
        ctr:
          totalClicksForQuery > 0
            ? topQueries[query].topClicked[productId] / totalClicksForQuery
            : 0
      });
    });

    // sort topClickedAccumulated by count descending
    topClickedAccumulated.sort((a, b) => b.count - a.count);

    topQueriesOutput.push({
      query,
      daysWithQuery: topQueries[query].daysWithQuery,
      count: topQueries[query].count,
      clickSessions: Math.round(topQueries[query].clickSessions),
      ctr:
        topQueries[query].count > 0 ? topQueries[query].clickSessions / topQueries[query].count : 0,
      topClicked: topClickedAccumulated
    });
  });

  // sort topQueriesOutput by count descending, excluding empty-query entries
  // (empty query = search window opened with no typing, clicks on recommended products)
  topQueriesOutput.sort((a, b) => b.count - a.count);
  const filteredTopQueries = topQueriesOutput.filter((q) => q.query !== '');

  const sortedItems = [...items].sort((a, b) => a.dateKey.localeCompare(b.dateKey));
  const chartLabels = sortedItems.map((item) => item.dateKey);
  const chartTopQueries = filteredTopQueries.slice(0, 10);
  const chartDataSeries = chartTopQueries.map((tq) =>
    sortedItems.map((item) => {
      const found = item.topQueries.find((q) => q.query === tq.query);
      return { x: item.dateKey, y: found ? found.count : 0, meta: { ctr: found ? found.ctr : 0 } };
    })
  );

  return {
    topQueries: filteredTopQueries,
    totalCount,
    totalRawSearchCount,
    totalDays: items.length,
    topQueriesChartData: {
      labels: chartLabels,
      dataSeries: chartDataSeries,
      dataTooltip: [],
      seriesLabels: chartTopQueries.map((tq) => tq.query)
    }
  };
}

function accummulateTopProducts(items) {
  const topProductsOutput = [];

  if (items.length < 1) {
    return { topProducts: topProductsOutput, totalCount: 0, totalRawSearchCount: 0, totalDays: 0 };
  }

  const topProducts = {};
  let totalCount = 0;
  let totalRawSearchCount = 0;
  let totalClickCount = 0;
  items.forEach((item) => {
    totalCount += item.searchCount;
    totalRawSearchCount += item.rawSearchCount;
    totalClickCount += item.clickCount || 0;
    item.topProducts.forEach((topProduct) => {
      if (!topProducts[topProduct.productId]) {
        topProducts[topProduct.productId] = {
          count: topProduct.count,
          daysWithProduct: 1
        };
      } else {
        topProducts[topProduct.productId].count += topProduct.count;
        topProducts[topProduct.productId].daysWithProduct += 1;
      }
    });
  });

  Object.keys(topProducts).forEach((productId) => {
    topProductsOutput.push({
      productId,
      daysWithProduct: topProducts[productId].daysWithProduct,
      count: topProducts[productId].count,
      ctr: topProducts[productId].count / totalCount
    });
  });

  // sort topProductsOutput by count descending
  topProductsOutput.sort((a, b) => b.count - a.count);
  return {
    topProducts: topProductsOutput,
    totalCount,
    totalRawSearchCount,
    totalClickCount,
    totalDays: items.length
  };
}

ProductWithLink.propTypes = {
  domain: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired
};

function ProductWithLink(props) {
  const { domain, productId } = props;
  const { t } = useTranslation();
  const { data, loading, error } = useQuery(GET_PRODUCT_DETAIL, {
    variables: { domain, productId },
    cacheTime: 5 * 60 * 1000, // 5 minutes
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
  return (
    <>
      {loading && <Skeleton variant="text" width={100} height={20} />}
      {error && <Alert severity="error">{t('errors.failed_to_load_product_detail')}</Alert>}
      {data && (
        <a
          href={data.getProduct?.link ?? '#'}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {data.getProduct?.title ?? t('errors.product_not_found')}
          <Icon icon={OpenInNewIcon} fontSize="small" />
        </a>
      )}
    </>
  );
}

TopQueriesRow.propTypes = {
  row: PropTypes.shape({
    query: PropTypes.string.isRequired,
    ctr: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
    clickSessions: PropTypes.number.isRequired,
    topClicked: PropTypes.arrayOf(
      PropTypes.shape({
        productId: PropTypes.string.isRequired,
        ctr: PropTypes.number.isRequired,
        count: PropTypes.number.isRequired
      })
    ).isRequired
  }).isRequired,
  domain: PropTypes.string.isRequired
};

function TopQueriesRow(props) {
  const { row, domain } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <Icon icon={KeyboardArrowUpIcon} /> : <Icon icon={KeyboardArrowDownIcon} />}
          </IconButton>
        </TableCell>
        <TableCell scope="row">{row.query}</TableCell>
        <TableCell align="right">{row.count}</TableCell>
        <TableCell align="right">
          {row.clickSessions}&nbsp;
          <Typography component="span" variant="caption" color="text.secondary">
            ({(row.ctr * 100).toFixed(0)}%)
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: 'grey.200' }}>
                  <TableRow>
                    <TableCell>{t('overview.top_clicked_for_query')}</TableCell>
                    <TableCell align="right">{t('general.click_share')}</TableCell>
                    <TableCell align="right">{t('general.count')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.topClicked.map((topClickedRow) => (
                    <TableRow key={topClickedRow.productId}>
                      <TableCell scope="row">
                        <ProductWithLink domain={domain} productId={topClickedRow.productId} />
                      </TableCell>
                      <TableCell align="right">{(topClickedRow.ctr * 100).toFixed(2)}%</TableCell>
                      <TableCell align="right">{topClickedRow.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function SearchOverview() {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    eshop: { domain, showSearch }
  } = useAccount();

  const [visibleQueries, setVisibleQueries] = useState(PAGE_SIZE);
  const [visibleProducts, setVisibleProducts] = useState(PAGE_SIZE);

  const { from, to, range, dispatch: dispatchPeriod } = usePeriod();
  const {
    loading: loadingQueries,
    error: errorQueries,
    data: dataQueries
  } = useQuery(GET_ADVANCED_STATISTICS, {
    variables: {
      domain,
      from: `topQueries-${from.clone().format('YYYY-MM-DD')}`,
      to: `topQueries-${to.clone().format('YYYY-MM-DD')}`
    },
    skip: !domain
  });

  const {
    loading: loadingProducts,
    error: errorProducts,
    data: dataProducts
  } = useQuery(GET_ADVANCED_STATISTICS, {
    variables: {
      domain,
      from: `topProducts-${from.clone().format('YYYY-MM-DD')}`,
      to: `topProducts-${to.clone().format('YYYY-MM-DD')}`
    },
    skip: !domain
  });

  useEffect(() => {
    setVisibleQueries(PAGE_SIZE);
    setVisibleProducts(PAGE_SIZE);
  }, [dataQueries, dataProducts]);

  // Extract and flatten topQueries from all items
  const statisticsQueries = dataQueries?.listAdvancedStatistics?.items
    ? accummulateTopQueries(dataQueries.listAdvancedStatistics.items)
    : {
        topQueries: [],
        totalCount: 0,
        totalRawSearchCount: 0,
        totalDays: 0,
        topQueriesChartData: {}
      };

  // Extract and flatten topProducts from all items
  // collapsable: https://mui.com/material-ui/react-table/
  const statisticsProducts = dataProducts?.listAdvancedStatistics?.items
    ? accummulateTopProducts(dataProducts.listAdvancedStatistics.items)
    : {
        topProducts: [],
        totalCount: 0,
        totalRawSearchCount: 0,
        totalClickCount: 0,
        totalDays: 0
      };

  return (
    <Page title={t('pages.search_overview')}>
      <Container>
        <Box>
          <Typography variant="h4" sx={{ mb: 5 }}>
            {t('general.search-overview')}
          </Typography>
        </Box>
        <Box>
          <PeriodSelector from={from} to={to} range={range} dispatch={dispatchPeriod} />
        </Box>
        {errorQueries && (
          <Alert severity="error">{t(`errors.failed_to_load_queries_statistics`)}</Alert>
        )}
        {errorProducts && (
          <Alert severity="error">{t(`errors.failed_to_load_products_statistics`)}</Alert>
        )}
        {!showSearch && (
          <ContainerMessage
            title={t('overview.search_not_active_title')}
            subtitle={t('overview.search_not_active_description')}
          />
        )}
        {/* show loading skeleton */}
        {(loadingQueries || loadingProducts) && (
          <>
            <Grid container spacing={3}>
              {[...Array(4)].map((_, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Skeleton
                    height={175}
                    variant="rectangular"
                    sx={{
                      borderRadius: `${theme.shape.borderRadius}px`
                    }}
                  />
                </Grid>
              ))}
              {[...Array(2)].map((_, index) => (
                <Grid item xs={12} md={12} lg={12} key={index}>
                  <Skeleton
                    height={450}
                    variant="rectangular"
                    sx={{
                      borderRadius: `${theme.shape.borderRadius}px`
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {showSearch &&
          !loadingQueries &&
          !loadingProducts &&
          statisticsQueries.topQueries.length === 0 &&
          statisticsProducts.topProducts.length === 0 && (
            <ContainerMessage
              title={t('overview.not_enough_data_title')}
              subtitle={t('overview.not_enough_data_description')}
            />
          )}
        {showSearch &&
          !loadingQueries &&
          !loadingProducts &&
          (statisticsQueries.topQueries.length > 0 ||
            statisticsProducts.topProducts.length > 0) && (
            <>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MultiChart
                    title={t('overview.top_queries_chart_title')}
                    seriesLabels={statisticsQueries.topQueriesChartData.seriesLabels}
                    yLabel={t('overview.top_queries_chart_y_label')}
                    dataSeries={statisticsQueries.topQueriesChartData.dataSeries}
                    dataTooltip={statisticsQueries.topQueriesChartData.dataTooltip}
                    labels={statisticsQueries.topQueriesChartData.labels}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <MetricCard
                    title={t('overview.total_result_clicks')}
                    number={statisticsProducts.totalClickCount}
                    tooltip={t('overview.total_result_clicks_tooltip')}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <MetricCard
                    title={t('overview.total_search_count')}
                    number={statisticsQueries.totalCount}
                    tooltip={t('overview.total_search_count_tooltip')}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <MetricCard
                    title={t('overview.total_raw_search_count')}
                    number={statisticsQueries.totalRawSearchCount}
                    tooltip={t('overview.total_raw_search_count_tooltip')}
                  />
                </Grid>
                {/* Top Queries */}
                <Grid item xs={12} sm={6} md={6}>
                  <TableContainer component={Card}>
                    <Table sx={{ minWidth: 500 }}>
                      <TableHead sx={{ backgroundColor: 'grey.200' }}>
                        <TableRow>
                          <TableCell width="60px" />
                          <TableCell scope="row">{t('overview.trending_queries')}</TableCell>
                          <TableCell align="right">
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end'
                              }}
                            >
                              {t('general.count')}
                              <HelperTooltip title={t('overview.trending_queries_count_tooltip')} />
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 110 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end'
                              }}
                            >
                              {t('general.clicks')}
                              <HelperTooltip
                                title={t('overview.trending_queries_clicks_tooltip')}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {statisticsQueries.topQueries.slice(0, visibleQueries).map((row) => (
                          <TopQueriesRow key={row.query} row={row} domain={domain} />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {statisticsQueries.topQueries.length > visibleQueries && (
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <Button size="small" onClick={() => setVisibleQueries((v) => v + PAGE_SIZE)}>
                        {t('general.load_more')}
                      </Button>
                    </Box>
                  )}
                </Grid>
                {/* Top products */}
                <Grid item xs={12} sm={6} md={6}>
                  <TableContainer component={Card}>
                    <Table sx={{ minWidth: 500 }}>
                      <TableHead sx={{ backgroundColor: 'grey.200' }}>
                        <TableRow>
                          <TableCell>{t('overview.trending_products')}</TableCell>
                          <TableCell align="right" sx={{ minWidth: 140 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end'
                              }}
                            >
                              {t('general.clicks')}
                              <HelperTooltip
                                title={t('overview.trending_products_clicks_tooltip')}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {statisticsProducts.topProducts.slice(0, visibleProducts).map((row) => (
                          <TableRow
                            key={row.productId}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                          >
                            <TableCell component="th" scope="row">
                              <ProductWithLink domain={domain} productId={row.productId} />
                            </TableCell>
                            <TableCell align="right">
                              {row.count}&nbsp;
                              <Typography component="span" variant="caption" color="text.secondary">
                                ({(row.ctr * 100).toFixed(0)}%)
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {statisticsProducts.topProducts.length > visibleProducts && (
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <Button size="small" onClick={() => setVisibleProducts((v) => v + PAGE_SIZE)}>
                        {t('general.load_more')}
                      </Button>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </>
          )}
      </Container>
    </Page>
  );
}
